import { Star, ExternalLink, Quote } from "lucide-react";
import { BRAND, GMB_URL, GMB_WRITE_URL, CURATED_REVIEWS } from "../mock";
import { openExternal } from "../lib/openExternal";

function GoogleG({ className = "w-5 h-5" }) {
  return (
    <svg viewBox="0 0 48 48" className={className} xmlns="http://www.w3.org/2000/svg">
      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303a12 12 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
      <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819A11.99 11.99 0 0 1 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36a12 12 0 0 1-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
    </svg>
  );
}

function Initial({ name }) {
  const letter = (name || "?").trim().charAt(0).toUpperCase();
  const palette = [
    "from-jester-purple to-jester",
    "from-acid to-[#7fb800]",
    "from-[#FDC814] to-[#e3a800]",
    "from-jester to-jester-purple",
    "from-[#ff5b8a] to-jester-purple",
    "from-acid to-jester",
  ];
  const color = palette[letter.charCodeAt(0) % palette.length];
  return (
    <div
      className={`w-12 h-12 rounded-full bg-gradient-to-br ${color} flex items-center justify-center font-display text-xl text-[#120127]`}
      data-testid="review-avatar"
    >
      {letter}
    </div>
  );
}

export default function CuratedReviews({
  compact = false,
  showHeading = true,
}) {
  return (
    <section
      id="live-reviews"
      className={`relative ${
        compact ? "py-20" : "py-28 md:py-36"
      } bg-[#0c0119] border-y border-white/5 overflow-hidden`}
      data-testid="curated-reviews-section"
    >
      <div className="absolute -top-20 right-1/4 w-[500px] h-[500px] rounded-full bg-jester-purple/10 blur-[140px]" />
      <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] rounded-full bg-acid/8 blur-[140px]" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {showHeading && (
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-acid mb-5 font-label flex items-center gap-2">
                <GoogleG className="w-4 h-4" /> Verified Google Reviews
              </div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
                Most reviewed<br />
                <span className="text-jester">ceramic coating shop</span><br />
                in the GTA.
              </h2>
              <p className="mt-6 text-white/75 text-lg max-w-xl leading-relaxed">
                1,500+ cars walking around Mississauga and Toronto with a finish
                you can actually see. and {BRAND.rating.count}+ five-star Google
                reviews to back it up.
              </p>
            </div>

            <a
              href={GMB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="google-card rounded-2xl p-6 flex items-center gap-5 group max-w-md"
              onClick={(e) => { e.preventDefault(); openExternal(GMB_URL); }}
              data-testid="google-rating-card"
            >
              <GoogleG className="w-10 h-10 flex-shrink-0" />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-stencil text-3xl text-white">
                    {BRAND.rating.value}
                  </span>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-4 h-4"
                        style={{ color: "#FDC814", fill: "#FDC814" }}
                      />
                    ))}
                  </div>
                </div>
                <p className="text-sm text-white/65">
                  Based on{" "}
                  <span className="text-acid font-semibold">
                    {BRAND.rating.count}+ Google reviews
                  </span>
                </p>
              </div>
              <ExternalLink className="w-5 h-5 text-white/40 group-hover:text-acid transition-colors" />
            </a>
          </div>
        )}

        {/* Review wall */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {CURATED_REVIEWS.map((r, idx) => (
            <article
              key={idx}
              className="relative rounded-2xl border border-white/8 bg-[#120127]/70 backdrop-blur-sm p-6 flex flex-col gap-4 hover:border-jester/40 hover:bg-[#120127]/90 transition-colors"
              data-testid={`review-card-${idx}`}
            >
              <Quote className="absolute top-5 right-5 w-7 h-7 text-jester-purple/30" />
              <div className="flex items-center gap-3">
                <Initial name={r.name} />
                <div className="flex-1 min-w-0">
                  <div className="text-white font-semibold truncate">
                    {r.name}
                  </div>
                  <div className="text-xs text-white/50">
                    {r.vehicle ? `${r.vehicle} · ` : ""}{r.when}
                  </div>
                </div>
                <GoogleG className="w-5 h-5 flex-shrink-0 opacity-70" />
              </div>
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4"
                    style={{ color: "#FDC814", fill: "#FDC814" }}
                  />
                ))}
              </div>
              <p className="text-white/85 text-[15px] leading-relaxed">
                &ldquo;{r.text}&rdquo;
              </p>
            </article>
          ))}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href={GMB_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-7 h-12 rounded-full bg-acid hover:bg-[#bff020] text-[#120127] font-label tracking-[0.2em] text-sm uppercase transition-colors shadow-[0_0_30px_rgba(168,224,0,0.3)]"
            onClick={(e) => { e.preventDefault(); openExternal(GMB_URL); }}
            data-testid="read-all-reviews-btn"
          >
            <GoogleG className="w-4 h-4" />
            Read all {BRAND.rating.count}+ reviews on Google
            <ExternalLink className="w-4 h-4" />
          </a>
          <a
            href={GMB_WRITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-7 h-12 rounded-full bg-jester-purple/15 border border-jester hover:bg-jester-purple/30 text-white font-label tracking-[0.2em] text-sm uppercase transition-colors"
            onClick={(e) => { e.preventDefault(); openExternal(GMB_WRITE_URL); }}
            data-testid="leave-a-review-btn"
          >
            <GoogleG className="w-4 h-4" />
            Leave a Google review
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
